/**
 * Client-side analytics tracker for visitor statistics
 * Stores data in localStorage (client-side only)
 */

export interface VisitData {
    timestamp: number;
    path: string;
    referrer: string;
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    isFirstVisit: boolean;
    sessionId: string;
    ip?: string;
    country?: string;
    countryCode?: string;
    city?: string;
    region?: string;
}

export interface AnalyticsStats {
    totalVisits: number;
    uniqueVisitors: number;
    visits: VisitData[];
    visitsByDate: Record<string, number>;
    visitsByPath: Record<string, number>;
    visitsByCountry: Record<string, number>;
    visitsByIP: Record<string, number>;
    recentVisits: VisitData[];
    firstVisit: number | null;
    lastVisit: number | null;
}

const STORAGE_KEY = 'maze_runner_analytics';
const VISITOR_ID_KEY = 'maze_runner_visitor_id';
const SESSION_ID_KEY = 'maze_runner_session_id';

class AnalyticsTracker {
    private getVisitorId(): string {
        let visitorId = localStorage.getItem(VISITOR_ID_KEY);
        if (!visitorId) {
            visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem(VISITOR_ID_KEY, visitorId);
        }
        return visitorId;
    }

    private getSessionId(): string {
        let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
        if (!sessionId) {
            sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem(SESSION_ID_KEY, sessionId);
        }
        return sessionId;
    }

    private getStoredStats(): AnalyticsStats {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Ensure backward compatibility - add missing fields if they don't exist
                return {
                    totalVisits: parsed.totalVisits || 0,
                    uniqueVisitors: parsed.uniqueVisitors || 0,
                    visits: parsed.visits || [],
                    visitsByDate: parsed.visitsByDate || {},
                    visitsByPath: parsed.visitsByPath || {},
                    visitsByCountry: parsed.visitsByCountry || {},
                    visitsByIP: parsed.visitsByIP || {},
                    recentVisits: parsed.recentVisits || [],
                    firstVisit: parsed.firstVisit || null,
                    lastVisit: parsed.lastVisit || null,
                };
            }
        } catch (e) {
            console.warn('Failed to parse stored analytics:', e);
        }
        return {
            totalVisits: 0,
            uniqueVisitors: 0,
            visits: [],
            visitsByDate: {},
            visitsByPath: {},
            visitsByCountry: {},
            visitsByIP: {},
            recentVisits: [],
            firstVisit: null,
            lastVisit: null,
        };
    }

    private saveStats(stats: AnalyticsStats): void {
        try {
            // Keep only last 1000 visits to prevent localStorage from growing too large
            if (stats.visits.length > 1000) {
                stats.visits = stats.visits.slice(-1000);
                stats.recentVisits = stats.recentVisits.slice(-100);
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
        } catch (e) {
            console.warn('Failed to save analytics:', e);
        }
    }

    private async fetchGeolocationData(): Promise<Partial<VisitData>> {
        const geoData: Partial<VisitData> = {};
        
        try {
            // Try ipapi.co first (free service, provides IP, country, city, region)
            const response = await fetch('https://ipapi.co/json/', {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                geoData.ip = data.ip || 'Unknown';
                geoData.country = data.country_name || 'Unknown';
                geoData.countryCode = (data.country_code || '').toUpperCase() || 'Unknown';
                geoData.city = data.city || 'Unknown';
                geoData.region = data.region || 'Unknown';
                return geoData;
            }
        } catch (error) {
            console.warn('Primary geolocation service failed:', error);
        }

        try {
            // Fallback to ipinfo.io
            const response = await fetch('https://ipinfo.io/json', {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                const [city, region] = (data.city || '').split(',');
                geoData.ip = data.ip || 'Unknown';
                geoData.country = data.country_name || 'Unknown';
                geoData.countryCode = (data.country || '').toUpperCase() || 'Unknown';
                geoData.city = city || data.city || 'Unknown';
                geoData.region = region || data.region || 'Unknown';
                return geoData;
            }
        } catch (error) {
            console.warn('Fallback geolocation service failed:', error);
        }

        // Default values if all methods fail
        return {
            ip: 'Unknown',
            country: 'Unknown',
            countryCode: 'Unknown',
            city: 'Unknown',
            region: 'Unknown',
        };
    }

    public async trackVisit(path: string = window.location.pathname): Promise<void> {
        try {
            // Fetch geolocation data first
            const geoData = await this.fetchGeolocationData();
            
            const stats = this.getStoredStats();
            const visitorId = this.getVisitorId();
            const sessionId = this.getSessionId();
            
            // Check if this is a new visitor
            const existingVisitorIds = new Set(
                stats.visits.map(v => {
                    // Extract visitor ID from sessionId pattern if possible
                    const match = v.sessionId?.match(/visitor_(\d+)/);
                    return match ? match[1] : null;
                }).filter((id): id is string => id !== null)
            );
            
            const isNewVisitor = !visitorId || (visitorId && !Array.from(existingVisitorIds).some(id => visitorId.includes(id)));

            const visitData: VisitData = {
                timestamp: Date.now(),
                path,
                referrer: document.referrer || 'direct',
                userAgent: navigator.userAgent,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                isFirstVisit: stats.totalVisits === 0,
                sessionId: `${visitorId}_${sessionId}`,
                ...geoData,
            };

            stats.visits.push(visitData);
            stats.totalVisits++;
            
            if (isNewVisitor) {
                stats.uniqueVisitors++;
            }

            // Update visits by date
            const dateKey = new Date(visitData.timestamp).toISOString().split('T')[0];
            stats.visitsByDate[dateKey] = (stats.visitsByDate[dateKey] || 0) + 1;

            // Update visits by path
            stats.visitsByPath[path] = (stats.visitsByPath[path] || 0) + 1;

            // Update visits by country
            const countryKey = visitData.country || 'Unknown';
            stats.visitsByCountry[countryKey] = (stats.visitsByCountry[countryKey] || 0) + 1;

            // Update visits by IP
            if (visitData.ip && visitData.ip !== 'Unknown') {
                stats.visitsByIP[visitData.ip] = (stats.visitsByIP[visitData.ip] || 0) + 1;
            }

            // Update recent visits (last 50)
            stats.recentVisits.push(visitData);
            if (stats.recentVisits.length > 50) {
                stats.recentVisits = stats.recentVisits.slice(-50);
            }

            // Update first and last visit
            if (!stats.firstVisit) {
                stats.firstVisit = visitData.timestamp;
            }
            stats.lastVisit = visitData.timestamp;

            this.saveStats(stats);
        } catch (e) {
            console.warn('Failed to track visit:', e);
        }
    }

    public getStats(): AnalyticsStats {
        return this.getStoredStats();
    }

    public clearStats(): void {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(VISITOR_ID_KEY);
        sessionStorage.removeItem(SESSION_ID_KEY);
    }

    public exportStats(): string {
        const stats = this.getStats();
        return JSON.stringify(stats, null, 2);
    }
}

export const analyticsTracker = new AnalyticsTracker();
